import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useSearch, useSearchInput } from '../contexts/SearchContext';
import { Grid, User, Heart, MessageSquare, Search, Bell, Menu, X, LogOut, ChevronLeft, ChevronRight, BarChart2, Shield, ExternalLink, BookOpen, Video, Users, Tag, Settings, FileText, Sliders, Calendar } from 'react-feather';
import AccountSettingsModal from './AccountSettingsModal';
import StaffAccountSettingsModal from './StaffAccountSettingsModal';
import CoachAccountSettingsModal from './CoachAccountSettingsModal';
import NotificationDropdown from './NotificationDropdown';
import { useConfirm } from '../hooks/useConfirm';
import { useResponsive } from '../hooks/useResponsive';
import { TextInput } from './ui';
import { IS_STAGING_BUILD } from '../config/environment';

interface AppLayoutProps {
  children: React.ReactNode;
  /**
   * Overrides the top bar's title. A route-table lookup cannot know the name of
   * the thing a detail page is showing, so /coach/players/:id used to read
   * "Players" while the page under it was one athlete's profile — and the
   * document carried two H1s saying different things.
   */
  pageTitle?: string;
  /**
   * Whether this layout acts on deep-link params (?openSettings=true) and
   * strips them from the URL. Defaults to true. A placeholder layout that is
   * about to be replaced by the real page (CoachRoute's boot skeleton) passes
   * false: if it consumed the param, the page's own layout would mount with
   * the modal closed and the param already gone.
   */
  consumeDeepLinks?: boolean;
}

/* The native <button> chrome the sidebar's account block must not show, so it
   looks the same as the <div> it replaced: a shrink-to-fit width, the UA
   border and background, the button font and colour, centred text. Margin,
   padding and radius are set per button. No outline here: the global
   :focus-visible ring has to show. */
const userBlockButtonReset: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: 'none',
  background: 'transparent',
  appearance: 'none',
  font: 'inherit',
  color: 'inherit',
  textAlign: 'start',
};

const AppLayout: React.FC<AppLayoutProps> = ({ children, pageTitle, consumeDeepLinks = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const { inputValue, setInputValue } = useSearchInput();
  const { handleSearch } = useSearch();
  const { isMobile, isTablet } = useResponsive();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);

  // Close mobile menu when navigating
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when switching to desktop
  useEffect(() => {
    if (!isMobile && !isTablet) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, isTablet]);

  /*
   * The nav drawer is an overlay, and the Filters sheet on the same screen
   * already behaves like one: named, role=dialog, Escape closes, focus moved.
   * The drawer did none of it — Escape left it open and both its controls had
   * an empty accessible name. Two overlays, two behaviours, one screen.
   */
  const menuButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const menuCloseRef = React.useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    // Focus the drawer's own dismiss, the way the filter sheet does.
    const t = setTimeout(() => menuCloseRef.current?.focus(), 0);
    /* And the page underneath holds still, the way it does under the Filters
       sheet and every Modal: an open drawer that lets the page scroll behind it
       loses the reader's place on a phone — the one place this drawer exists. */
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileMenuOpen]);

  // Open account settings modal if query param is present
  useEffect(() => {
    if (!consumeDeepLinks) return;
    if (searchParams.get('openSettings') === 'true') {
      setAccountSettingsOpen(true);
      // Remove the query param from URL
      searchParams.delete('openSettings');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, consumeDeepLinks]);

  const { ConfirmComponent: SignOutConfirm, confirm: confirmSignOut } = useConfirm({
    title: 'Sign out',
    message: 'Are you sure you want to sign out of your account?',
    confirmText: 'Sign out',
    cancelText: 'Cancel',
    variant: 'warning',
  });

  const [adminExpanded, setAdminExpanded] = useState(
    location.pathname.startsWith('/admin')
  );

  const [staffExpanded, setStaffExpanded] = useState(
    location.pathname.startsWith('/staff')
  );

  const [resourcesExpanded, setResourcesExpanded] = useState(false);

  const navigationItems = [
    {
      id: 'dashboard',
      // The page is purely college search + matched/open/all lists (no home/overview),
      // so name it for what it does and match mobile's mental model. Route id unchanged.
      label: 'Find Colleges',
      path: '/dashboard',
      icon: <Search size={20} />
    },
    {
      // Discover (Find Colleges) and My Lists are the two halves of one job, so
      // they sit ADJACENT and the nav is the switcher — no separate in-page
      // toggle (which used to duplicate this and split the two with Profile).
      id: 'colleges',
      label: 'My Lists',
      path: '/colleges',
      icon: <Heart size={20} />
    },
    {
      id: 'profile',
      label: 'My Profile',
      path: '/profile',
      icon: <User size={20} />
    },
    {
      id: 'messages',
      label: 'Messages',
      path: '/messages',
      icon: <MessageSquare size={20} />
    },
    {
      id: 'analytics',
      label: 'Analytics',
      path: '/analytics',
      icon: <BarChart2 size={20} />
    }
  ];

  const adminItems = can('*:*') ? [
    {
      id: 'admin-users',
      label: 'Manage Users',
      path: '/admin/users',
    },
    {
      id: 'admin-athletes',
      label: 'Manage Athletes',
      path: '/admin/manage-athletes',
    },
    {
      // The internal verification desk (KGH-795). "College Coaches" rather than
      // "Coaches": a super-admin also sees staff (club) coaching surfaces, and
      // these are the college programs' own coaches.
      //
      // Sits with Users and Athletes rather than at the end of the list, which
      // is only where it landed because it was built last. These three are the
      // "who is in the product" surfaces; everything below is commercial
      // (promo/partners/payouts), structural (organizations) or reporting. It is
      // also the only entry here that carries a work queue somebody is waiting
      // on, which is a poor fit for the bottom of a ten-item menu.
      id: 'admin-coaches',
      label: 'College Coaches',
      path: '/admin/coaches',
    },
    // Staging only: every email staging sends, so a flow can be checked end to
    // end. A production bundle has no such page, so the item is not built there.
    ...(IS_STAGING_BUILD
      ? [
          {
            id: 'admin-outbox',
            label: 'Email Outbox',
            path: '/admin/outbox',
          },
        ]
      : []),
    {
      id: 'admin-queues',
      label: 'Queue Monitor',
      path: '/admin/queues',
    },
    {
      id: 'admin-promo',
      label: 'Promo Codes',
      path: '/admin/promo-codes',
    },
    {
      id: 'admin-partners',
      label: 'Partners',
      path: '/admin/partners',
    },
    {
      id: 'admin-payouts',
      label: 'Payouts',
      path: '/admin/payouts',
    },
    {
      id: 'admin-organizations',
      label: 'Organizations',
      path: '/admin/organizations',
    },
    {
      id: 'admin-analytics',
      // Disambiguated from the staff (coaching) "Analytics" a super-admin also sees.
      label: 'Platform Analytics',
      path: '/admin/analytics',
    },
  ] : can('promo:*') ? [
    {
      id: 'admin-promo',
      label: 'Promo Codes',
      path: '/admin/promo-codes',
    }
  ] : [];

  const partnerItems = [
    {
      id: 'best-colleges',
      label: 'Best Colleges (Niche)',
      url: 'https://www.niche.com/colleges/search/best-colleges/',
    },
    {
      id: 'college-advisors',
      label: 'College Advisors',
      url: 'https://www.collegeadvisor.com/',
    },
    {
      id: 'diamond-dynamics',
      label: 'Diamond Dynamics',
      url: 'https://www.diamonddynamics.org',
    },
    {
      id: 'different-softball',
      label: 'Different Softball Exp',
      url: 'https://www.differentsoftballexp.com',
    },
    {
      id: 'extra-inning-softball',
      label: 'Extra Inning Softball',
      url: 'https://extrainningsoftball.com/',
    },
    {
      id: 'jen-schro-catching',
      label: 'Jen Schro Catching',
      url: 'https://www.jenschrocatching.com',
    },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isStaffActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  // A guardian/athlete who also has staff access (super admin OR linked as org_staff
  // via the "add existing user as staff" flow). They keep their family nav and get
  // a Staff dropdown overlaid.
  const isFamilyWithStaffAccess = (user?.user_type === 'guardian' || user?.user_type === 'athlete')
    && (can('*:*') || !!user?.organization_id);
  // Pure-staff nav: user_type='staff' OR super admin not in family role.
  const isStaff = (user?.user_type === 'staff' || can('*:*')) && !isFamilyWithStaffAccess;

  const staffNavItems = [
    { id: 'staff-roster', label: 'Player Roster', path: '/staff/roster', icon: <User size={20} /> },
    { id: 'staff-messages', label: 'Messages', path: '/staff/messages', icon: <MessageSquare size={20} /> },
    // Browse the full catalog vs. your saved lists — two adjacent destinations
    // (mirrors the athlete Find Colleges / My Lists model), no in-page toggle.
    { id: 'staff-all-schools', label: 'Schools', path: '/staff/all-schools', icon: <Grid size={20} /> },
    { id: 'staff-lists', label: 'My Lists', path: '/staff/lists', icon: <Heart size={20} /> },
    { id: 'staff-analytics', label: 'Analytics', path: '/staff/analytics', icon: <BarChart2 size={20} /> },
    // School Report is an aggregation across players' lists — a report, not a
    // favorites list — so it gets a document icon (Heart is reserved for saved).
    { id: 'staff-schools', label: 'School Report', path: '/staff/schools', icon: <FileText size={20} /> },
  ];

  // A verified college coach (KGH-795). Four destinations, because that is the
  // whole job: find athletes, see where they will be playing, keep the ones
  // worth keeping, and tell us what you recruit so the ratings mean something.
  const coachNavItems = [
    { id: 'coach-players', label: 'Players', path: '/coach/players', icon: <Users size={20} /> },
    // APS-255 items 6 + 11: the crowd-sourced tournament list. It sits next to
    // Players because it is the same job done by place and date instead of by
    // filter, and a coach on the road reaches for it first.
    { id: 'coach-tournaments', label: 'Tournaments', path: '/coach/tournaments', icon: <Calendar size={20} /> },
    { id: 'coach-lists', label: 'My Lists', path: '/coach/lists', icon: <Heart size={20} /> },
    { id: 'coach-preferences', label: 'Recruiting Profile', path: '/coach/preferences', icon: <Sliders size={20} /> },
  ];

  // Nav was a binary — `isStaff ? staff : family` repeated a dozen times — and a
  // coach is neither: user_type 'coach' holds no '*:*' permission, so they fell
  // through to the FAMILY nav and were handed five destinations that all 403.
  // Resolved once here instead, so a third role cannot be half-applied.
  const role: 'coach' | 'staff' | 'family' =
    user?.user_type === 'coach' ? 'coach' : isStaff ? 'staff' : 'family';
  const navItems = role === 'coach' ? coachNavItems : role === 'staff' ? staffNavItems : navigationItems;
  // Family routes are flat and match exactly; staff and coach routes nest.
  const activeFor = role === 'family' ? isActive : isStaffActive;

  const handleLogout = async () => {
    const confirmed = await confirmSignOut();
    if (confirmed) {
      logout();
    }
  };

  return (
    <>
      <SignOutConfirm />
      <div style={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        background: '#fafbff',
        fontFamily: 'Manrope, sans-serif'
      }}>
        {/* Mobile Menu Backdrop */}
        {(isMobile || isTablet) && mobileMenuOpen && (
          <div 
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 998,
              transition: 'opacity 0.3s ease',
            }}
          />
        )}

        {/* Sidebar */}
      <div
        id="aps-mobile-nav"
        {...((isMobile || isTablet) && mobileMenuOpen
          ? { role: 'dialog' as const, 'aria-modal': true, 'aria-label': 'Main menu' }
          : {})}
        style={{
        width: (isMobile || isTablet) ? '280px' : (sidebarCollapsed ? '80px' : '260px'),
        height: '100%',
        background: 'linear-gradient(180deg, #151133 0%, #1a1640 100%)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        position: (isMobile || isTablet) ? 'fixed' : 'relative',
        left: (isMobile || isTablet) ? (mobileMenuOpen ? '0' : '-280px') : '0',
        top: 0,
        bottom: 0,
        boxShadow: '2px 0 12px rgba(0,0,0,0.1)',
        zIndex: 999,
        overflow: 'hidden',
      }}>
        {/* Logo / Brand */}
        <div style={{
          padding: sidebarCollapsed && !isMobile && !isTablet ? '24px 16px' : '24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed && !isMobile && !isTablet ? 'center' : 'space-between'
        }}>
          {(!sidebarCollapsed || isMobile || isTablet) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                background: 'white',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px'
              }}>
                <img 
                  src="/images/logo-180.png" 
                  alt="APS" 
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
              <span style={{
                fontSize: '18px',
                fontWeight: '700',
                color: 'white'
              }}>
                APS Recruits
              </span>
            </div>
          )}
          {sidebarCollapsed && !isMobile && !isTablet && (
            <div style={{
              width: '36px',
              height: '36px',
              background: 'white',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px'
            }}>
              <img 
                src="/images/logo-180.png" 
                alt="APS" 
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
          )}
          {/* Close button for mobile */}
          {(isMobile || isTablet) && (
            <button
              ref={menuCloseRef}
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={20} color="white" />
            </button>
          )}
        </div>

        {/* Navigation Items */}
        <nav style={{
          flex: 1,
          minHeight: 0,
          padding: '16px 12px',
          overflowY: 'auto',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => {
                navigate(item.path);
                if (isMobile || isTablet) setMobileMenuOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: sidebarCollapsed && !isMobile && !isTablet ? '12px' : '12px 16px',
                background: activeFor(item.path)
                  ? 'rgba(249, 85, 13, 0.15)' 
                  : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: activeFor(item.path)
                  ? '#f9550d' 
                  : 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                fontWeight: activeFor(item.path) ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: 'Manrope, sans-serif',
                justifyContent: sidebarCollapsed && !isMobile && !isTablet ? 'center' : 'flex-start',
                position: 'relative',
                textAlign: 'left',
                minHeight: '44px', // Touch-friendly
              }}
              onMouseEnter={(e) => {
                if (!activeFor(item.path)) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (!activeFor(item.path)) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                }
              }}
            >
              {activeFor(item.path) && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '3px',
                  height: '60%',
                  background: '#f9550d',
                  borderRadius: '0 2px 2px 0'
                }} />
              )}
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {item.icon}
              </span>
              {(!sidebarCollapsed || isMobile || isTablet) && <span>{item.label}</span>}
            </button>
          ))}

          {/* Collapsed Resources Icon */}
          {role === 'family' && sidebarCollapsed && !isMobile && !isTablet && (
            <button
              onClick={() => {
                setSidebarCollapsed(false);
                setResourcesExpanded(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px',
                background: 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: 'rgba(255, 255, 255, 0.7)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
              }}
            >
              <BookOpen size={20} />
            </button>
          )}

          {/* Resources Section */}
          {role === 'family' && (!sidebarCollapsed || isMobile || isTablet) && (
            <>
              {/* Resources Expandable Menu */}
              <button
                onClick={() => setResourcesExpanded(!resourcesExpanded)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Manrope, sans-serif',
                  textAlign: 'left',
                  position: 'relative',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
                  <BookOpen size={20} style={{ flexShrink: 0 }} />
                  <span>Resources</span>
                </div>
                <ChevronRight 
                  size={16} 
                  style={{ 
                    transform: resourcesExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                    flexShrink: 0
                  }} 
                />
              </button>

              {/* Resources Submenu Items */}
              {resourcesExpanded && (
                <div style={{ 
                  paddingLeft: '16px',
                  marginTop: '4px',
                  overflow: 'hidden'
                }}>
                  {/* Tutorials Link */}
                  <button
                    onClick={() => navigate('/tutorials')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      width: '100%',
                      padding: '10px 16px',
                      background: isActive('/tutorials') ? 'rgba(249, 85, 13, 0.15)' : 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      color: isActive('/tutorials') ? '#f9550d' : 'rgba(255, 255, 255, 0.7)',
                      fontSize: '13px',
                      fontWeight: isActive('/tutorials') ? '600' : '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Manrope, sans-serif',
                      textAlign: 'left',
                      marginBottom: '2px',
                      minHeight: '40px',
                      boxSizing: 'border-box',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive('/tutorials')) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.color = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive('/tutorials')) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                      }
                    }}
                  >
                    <Video size={15} style={{ flexShrink: 0, opacity: 0.8 }} />
                    <span>Tutorials</span>
                  </button>

                  {/* Partners Sub-header */}
                  <div style={{
                    fontSize: '10px',
                    fontWeight: '700',
                    color: 'rgba(255, 255, 255, 0.35)',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    padding: '12px 16px 6px 16px',
                  }}>
                    Partners
                  </div>

                  {/* Partner Links */}
                  {partnerItems.map((item) => (
                    <a
                      key={item.id}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        padding: '10px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '6px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Manrope, sans-serif',
                        textAlign: 'left',
                        textDecoration: 'none',
                        marginBottom: '2px',
                        minHeight: '40px',
                        boxSizing: 'border-box',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                      <ExternalLink size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
                    </a>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Staff Section - for admin users who also have athlete/guardian accounts */}
          {isFamilyWithStaffAccess && (!sidebarCollapsed || isMobile || isTablet) && (
            <>
              <div style={{
                height: '1px',
                background: 'rgba(255, 255, 255, 0.1)',
                margin: '12px 0'
              }} />

              {/* Staff Expandable Menu */}
              <button
                onClick={() => setStaffExpanded(!staffExpanded)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '12px 16px',
                  background: location.pathname.startsWith('/staff')
                    ? 'rgba(139, 92, 246, 0.15)'
                    : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: location.pathname.startsWith('/staff')
                    ? '#a78bfa'
                    : 'rgba(196, 181, 253, 0.9)',
                  fontSize: '14px',
                  fontWeight: location.pathname.startsWith('/staff') ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Manrope, sans-serif',
                  textAlign: 'left',
                  position: 'relative',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  if (!location.pathname.startsWith('/staff')) {
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                    e.currentTarget.style.color = '#c4b5fd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!location.pathname.startsWith('/staff')) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(196, 181, 253, 0.9)';
                  }
                }}
              >
                {location.pathname.startsWith('/staff') && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '60%',
                    background: '#a78bfa',
                    borderRadius: '0 2px 2px 0'
                  }} />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Users size={20} />
                  <span>Staff</span>
                </div>
                <ChevronRight 
                  size={16} 
                  style={{ 
                    transform: staffExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </button>

              {/* Staff Submenu Items */}
              {staffExpanded && (
                <div style={{ 
                  paddingLeft: '16px',
                  marginTop: '4px'
                }}>
                  {staffNavItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        navigate(item.path);
                        if (isMobile || isTablet) setMobileMenuOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 16px',
                        background: location.pathname.startsWith(item.path)
                          ? 'rgba(139, 92, 246, 0.1)'
                          : 'transparent',
                        border: 'none',
                        borderRadius: '6px',
                        color: location.pathname.startsWith(item.path)
                          ? '#a78bfa'
                          : 'rgba(196, 181, 253, 0.7)',
                        fontSize: '13px',
                        fontWeight: location.pathname.startsWith(item.path) ? '600' : '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Manrope, sans-serif',
                        textAlign: 'left',
                        width: '100%',
                        marginBottom: '2px',
                        minHeight: '40px',
                      }}
                      onMouseEnter={(e) => {
                        if (!location.pathname.startsWith(item.path)) {
                          e.currentTarget.style.background = 'rgba(139, 92, 246, 0.05)';
                          e.currentTarget.style.color = '#c4b5fd';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!location.pathname.startsWith(item.path)) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'rgba(196, 181, 253, 0.7)';
                        }
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Collapsed Staff Icon */}
          {isFamilyWithStaffAccess && sidebarCollapsed && !isMobile && !isTablet && (
            <>
              <div style={{
                height: '1px',
                background: 'rgba(255, 255, 255, 0.1)',
                margin: '12px 0'
              }} />
              <button
                onClick={() => {
                  setSidebarCollapsed(false);
                  setStaffExpanded(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px',
                  background: location.pathname.startsWith('/staff')
                    ? 'rgba(139, 92, 246, 0.15)'
                    : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: location.pathname.startsWith('/staff')
                    ? '#a78bfa'
                    : 'rgba(196, 181, 253, 0.9)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!location.pathname.startsWith('/staff')) {
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                    e.currentTarget.style.color = '#c4b5fd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!location.pathname.startsWith('/staff')) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(196, 181, 253, 0.9)';
                  }
                }}
              >
                {location.pathname.startsWith('/staff') && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '60%',
                    background: '#a78bfa',
                    borderRadius: '0 2px 2px 0'
                  }} />
                )}
                <Users size={20} />
              </button>
            </>
          )}

          {/* Admin Section */}
          {adminItems.length > 0 && (!sidebarCollapsed || isMobile || isTablet) && (
            <>
              <div style={{
                height: '1px',
                background: 'rgba(255, 255, 255, 0.1)',
                margin: '12px 0'
              }} />

              {/* Admin Expandable Menu */}
              <button
                onClick={() => setAdminExpanded(!adminExpanded)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '12px 16px',
                  background: location.pathname.startsWith('/admin')
                    ? 'rgba(59, 130, 246, 0.15)'
                    : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: location.pathname.startsWith('/admin')
                    ? '#60a5fa'
                    : 'rgba(147, 197, 253, 0.9)',
                  fontSize: '14px',
                  fontWeight: location.pathname.startsWith('/admin') ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Manrope, sans-serif',
                  textAlign: 'left',
                  position: 'relative',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  if (!location.pathname.startsWith('/admin')) {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                    e.currentTarget.style.color = '#93c5fd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!location.pathname.startsWith('/admin')) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(147, 197, 253, 0.9)';
                  }
                }}
              >
                {location.pathname.startsWith('/admin') && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '60%',
                    background: '#60a5fa',
                    borderRadius: '0 2px 2px 0'
                  }} />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Shield size={20} />
                  <span>Admin</span>
                </div>
                <ChevronRight 
                  size={16} 
                  style={{ 
                    transform: adminExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </button>

              {/* Admin Submenu Items */}
              {adminExpanded && (
                <div style={{ 
                  paddingLeft: '16px',
                  marginTop: '4px'
                }}>
                  {adminItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        navigate(item.path);
                        if (isMobile || isTablet) setMobileMenuOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 16px',
                        background: location.pathname === item.path
                          ? 'rgba(59, 130, 246, 0.1)'
                          : 'transparent',
                        border: 'none',
                        borderRadius: '6px',
                        color: location.pathname === item.path
                          ? '#60a5fa'
                          : 'rgba(147, 197, 253, 0.7)',
                        fontSize: '13px',
                        fontWeight: location.pathname === item.path ? '600' : '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Manrope, sans-serif',
                        textAlign: 'left',
                        width: '100%',
                        marginBottom: '2px',
                        minHeight: '40px', // Touch-friendly
                      }}
                      onMouseEnter={(e) => {
                        if (location.pathname !== item.path) {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
                          e.currentTarget.style.color = '#93c5fd';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (location.pathname !== item.path) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'rgba(147, 197, 253, 0.7)';
                        }
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Collapsed Admin Icon */}
          {adminItems.length > 0 && sidebarCollapsed && !isMobile && !isTablet && (
            <>
              <div style={{
                height: '1px',
                background: 'rgba(255, 255, 255, 0.1)',
                margin: '12px 0'
              }} />
              <button
                onClick={() => {
                  setSidebarCollapsed(false);
                  setAdminExpanded(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px',
                  background: location.pathname.startsWith('/admin')
                    ? 'rgba(59, 130, 246, 0.15)'
                    : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: location.pathname.startsWith('/admin')
                    ? '#60a5fa'
                    : 'rgba(147, 197, 253, 0.9)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!location.pathname.startsWith('/admin')) {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                    e.currentTarget.style.color = '#93c5fd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!location.pathname.startsWith('/admin')) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(147, 197, 253, 0.9)';
                  }
                }}
              >
                {location.pathname.startsWith('/admin') && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '60%',
                    background: '#60a5fa',
                    borderRadius: '0 2px 2px 0'
                  }} />
                )}
                <Shield size={20} />
              </button>
            </>
          )}
          </div>
        </nav>

        {/* User Profile Section */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          {(!sidebarCollapsed || isMobile || isTablet) ? (
            /* A real button, so a keyboard can reach and open Account settings.
               The reset (width, border, background, margin, font, colour,
               alignment) keeps it looking exactly like the block it replaced.
               No inline outline: the global :focus-visible ring must show. */
            <button
            type="button"
            aria-label="Account settings"
            style={{
              ...userBlockButtonReset,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: '0 0 12px',
              padding: '8px',
              borderRadius: '8px',
              transition: 'background 0.2s ease',
              cursor: 'pointer',
              // Touch-friendly. 60 includes the padding: the <div> this replaced
              // measured its 44 on the content box, a button counts the padding.
              minHeight: '60px',
            }}
            onClick={() => {
              setAccountSettingsOpen(true);
              if (isMobile || isTablet) setMobileMenuOpen(false);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white'
              }}>
                {(user as any)?.name?.charAt(0) || user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {(user as any)?.name || user?.email?.split('@')[0] || 'User'}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {user?.email || ''}
                </div>
              </div>
              {/* Visible Settings affordance — the block was clickable but nothing
                  signalled it opened settings. */}
              <Settings size={18} color="rgba(255,255,255,0.55)" style={{ flexShrink: 0 }} aria-hidden="true" />
            </button>
          ) : (
            <button
            type="button"
            aria-label="Account settings"
            style={{
              ...userBlockButtonReset,
              display: 'flex',
              justifyContent: 'center',
              margin: '0 0 12px',
              padding: 0,
              cursor: 'pointer',
            }}
            onClick={() => setAccountSettingsOpen(true)}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white'
              }}>
                {(user as any)?.name?.charAt(0) || user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
            </button>
          )}

          {/* Collapse Toggle & Sign Out */}
          <div style={{
            display: 'flex',
            gap: '8px',
            flexDirection: (sidebarCollapsed && !isMobile && !isTablet) ? 'column' : 'row'
          }}>
            {/* Hide collapse button on mobile */}
            {!isMobile && !isTablet && (
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                style={{
                  flex: sidebarCollapsed ? 'none' : 1,
                  padding: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Manrope, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  minHeight: '36px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                {!sidebarCollapsed && <span>Collapse</span>}
              </button>
            )}
            
            <button
              onClick={handleLogout}
              style={{
                flex: (sidebarCollapsed && !isMobile && !isTablet) ? 'none' : 1,
                padding: '8px 12px',
                background: 'rgba(220, 38, 38, 0.15)',
                border: 'none',
                borderRadius: '6px',
                color: '#ef4444',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: 'Manrope, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                minHeight: '44px', // Touch-friendly
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(220, 38, 38, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(220, 38, 38, 0.15)';
              }}
            >
              <LogOut size={14} />
              {/* Always show text on mobile, or when sidebar not collapsed */}
              {(!sidebarCollapsed || isMobile || isTablet) && <span>Sign out</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Top Header Bar */}
        <div style={{
          height: '70px',
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: (isMobile || isTablet) ? '0 16px' : '0 32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          gap: '12px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flex: 1,
            minWidth: 0,
          }}>
            {/* Hamburger Menu Button - Mobile/Tablet Only */}
            {(isMobile || isTablet) && (
              <button
                ref={menuButtonRef}
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="aps-mobile-nav"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '44px',
                  height: '44px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#151133',
                  flexShrink: 0,
                }}
              >
                <Menu size={24} />
              </button>
            )}
            {/* APS Logo - Mobile/Tablet Only */}
            {(isMobile || isTablet) && (
              <img 
                src="/images/logo-180.png" 
                alt="APS Recruits" 
                style={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'contain',
                  flexShrink: 0,
                  borderRadius: '8px',
                  boxShadow: '0 0 8px rgba(249, 85, 13, 0.4)',
                }}
              />
            )}
            <h1 style={{
              margin: 0,
              fontSize: isMobile ? '18px' : '24px',
              fontWeight: '700',
              color: '#151133',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {pageTitle || (() => {
                // Check for admin pages
                const adminItem = adminItems.find(item => item.path === location.pathname);
                if (adminItem) return adminItem.label;
                
                // Check for coach pages
                if (role === 'coach') {
                  const coachItem = coachNavItems.find(item => location.pathname.startsWith(item.path));
                  if (coachItem) return coachItem.label;
                }

                // Check for staff pages
                if (isStaff || isFamilyWithStaffAccess) {
                  const staffItem = staffNavItems.find(item => location.pathname.startsWith(item.path));
                  if (staffItem) return staffItem.label;
                }
                
                // Check for regular pages
                const navItem = navigationItems.find(item => item.path === location.pathname);
                if (navItem) return navItem.label;

                // Settings / utility pages
                if (location.pathname === '/settings/connections') return 'Social Connections';

                // Default
                return 'APS Recruits';
              })()}
            </h1>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '8px' : '16px',
            flexShrink: 0,
          }}>
            {/* Search Bar - Collapsible on Mobile (hidden for staff/admin) */}
            {role !== 'family' ? null : isMobile ? (
              <>
                {/* Mobile: Show search icon button or expanded search */}
                {mobileSearchOpen ? (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '70px',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 12px',
                    gap: '8px',
                    zIndex: 1001,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}>
                    {/* The magnifier is the primitive's leftIcon now, so the
                        absolutely-positioned copy and the onFocus/onBlur border
                        swap both go — along with the inline `outline: none`
                        that was cancelling the real focus ring. */}
                    <div style={{ flex: 1 }}>
                      <TextInput
                        type="text"
                        placeholder="Search schools..."
                        aria-label="Search schools"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && inputValue.trim()) {
                            handleSearch();
                            setMobileSearchOpen(false);
                          }
                        }}
                        autoFocus
                        leftIcon={<Search size={18} />}
                        style={{
                          padding: '12px 16px 12px 40px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '16px', // Prevent zoom on iOS
                        }}
                      />
                    </div>
                    <button
                      onClick={() => setMobileSearchOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '44px',
                        height: '44px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: '#6b7280',
                        flexShrink: 0,
                      }}
                    >
                      <X size={20} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setMobileSearchOpen(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '44px',
                      height: '44px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: '#6b7280',
                    }}
                  >
                    <Search size={20} />
                  </button>
                )}
              </>
            ) : (
              /* Desktop/Tablet: Normal search bar */
              /* Same swap as the mobile bar above, and the fixed 220/280px
                 width stays on the box so the header does not reflow. */
              <TextInput
                type="text"
                placeholder="Search schools..."
                aria-label="Search schools"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && inputValue.trim()) {
                    handleSearch();
                  }
                }}
                leftIcon={<Search size={18} />}
                style={{
                  padding: '8px 16px 8px 40px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  width: isTablet ? '220px' : '280px',
                }}
              />
            )}

            {/* Notifications Icon */}
            <NotificationDropdown />
          </div>
        </div>

        {/* Page Content */}
        <div data-app-scroll style={{
          flex: 1,
          overflow: 'auto',
          background: '#fafbff'
        }}>
          {children}
        </div>
      </div>

      {/* Account Settings Modal */}
      {role === 'coach' ? (
        /* A college coach has no family, subscription or messaging, so they get
         * their own small modal: name and title, sign-in email, notifications. */
        <CoachAccountSettingsModal
          isOpen={accountSettingsOpen}
          onClose={() => setAccountSettingsOpen(false)}
        />
      ) : role === 'staff' ? (
        <StaffAccountSettingsModal
          isOpen={accountSettingsOpen}
          onClose={() => setAccountSettingsOpen(false)}
          canEditProfile={role === 'staff'}
        />
      ) : (
        <AccountSettingsModal 
          isOpen={accountSettingsOpen} 
          onClose={() => setAccountSettingsOpen(false)} 
        />
      )}
    </div>
    </>
  );
};

export default AppLayout;
